import $ from 'jquery';
import _ from 'underscore';
import Backbone from "backbone";
import Chaplin from "chaplin";
import SettingsPage2View from "views/settingsPage/SettingsPage2View";
import PersistentModels from "services/PersistentModels";
import Utils from "models/helper/Utils";
import TrebbleClientAPIHelper from "models/helper/TrebbleClientAPI";
import LocalStorageHelper from "models/helper/LocalStorageHelper";
import PushNotificationHelper from "models/helper/PushNotificationHelper";
import RolloutHelper from "models/helper/FeatureRolloutHelper";
import StripeHelper from "models/helper/StripeHelper";
import SubscriptionManager from "models/helper/SubscriptionManager";
import WorkspacePricingCatalog from "models/helper/WorkspacePricingCatalog";
import { shouldShowUpgradeCTA, getRecommendedUpgradeMode } from "models/helper/WorkspaceUpgradeCTA";
import ti18n from 'i18n!nls/SettingsPagei18n';
import workspaceTi18n from 'i18n!nls/WorkspaceManagei18n';
import RSVP from "rsvp";

const Rollout = RolloutHelper.getInstance();
// Default both flags to ON. Optimizely can still override per-environment,
// but in the absence of an Optimizely entry the feature is on so the
// default-workspace synthesis path runs for free users without a real
// workspace doc. Without a default of `true`, isFeatureEnabled returns
// undefined → falsy, and the controller never loads any workspace data.
const WORKSPACE_ENABLED = Rollout.isFeatureEnabled(Rollout.FEATURES.WORKSPACE_ENABLED, true);
const USE_REAL_WORKSPACE_DATA = Rollout.isFeatureEnabled(Rollout.FEATURES.USE_REAL_WORKSPACE_DATA, true);

// Plan defaults for the settings billing panel. Pricing is sourced from
// models/helper/WorkspacePricingCatalog.js so every billing surface stays in
// sync with the published price list. "Unlimited" is rendered for the UI when
// the catalog's reviewersPerMonth is null.
function _formatReviewerLimit(value) {
    return value === null || value === undefined ? "Unlimited" : value;
}
function _planDefault(planType, displayName) {
    const price = WorkspacePricingCatalog.FALLBACK_PLAN_PRICES[planType];
    const feat = WorkspacePricingCatalog.FALLBACK_PLAN_FEATURES[planType];
    return {
        name: displayName,
        price: WorkspacePricingCatalog.formatCurrencyAmount(price.monthly, price.currency).replace(/\.00$/, ""),
        hours: feat.hours,
        storageGB: feat.storageGB,
        editors: feat.editors,
        reviewers: _formatReviewerLimit(feat.reviewersPerMonth),
    };
}
const PLAN_DEFAULTS = {
    free: _planDefault("free", "Free"),
    starter: _planDefault("starter", "Starter"),
    pro: _planDefault("pro", "Pro"),
    team: _planDefault("team", "Team"),
};

function hasValue(value) {
    return value !== undefined && value !== null && value !== "";
}

function toNumber(value) {
    if (!hasValue(value)) return null;
    var numericValue = typeof value === "number" ? value : Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
}

function roundToOneDecimal(value) {
    var numericValue = toNumber(value);
    if (numericValue === null) return null;
    return Math.round(numericValue * 10) / 10;
}

function minutesToHours(value) {
    var numericValue = toNumber(value);
    if (numericValue === null) return null;
    return roundToOneDecimal(numericValue / 60);
}

function bytesToGigabytes(value) {
    var numericValue = toNumber(value);
    if (numericValue === null) return null;
    return roundToOneDecimal(numericValue / (1024 * 1024 * 1024));
}

function getValueAtPath(source, path) {
    if (!source || !path) return null;
    var pathParts = Array.isArray(path) ? path : String(path).split(".");
    var currentValue = source;
    for (var i = 0; i < pathParts.length; i++) {
        if (currentValue === null || currentValue === undefined) {
            return null;
        }
        currentValue = currentValue[pathParts[i]];
    }
    return hasValue(currentValue) ? currentValue : null;
}

function getFirstValueFromSources(sources, paths) {
    for (var sourceIndex = 0; sourceIndex < sources.length; sourceIndex++) {
        var source = sources[sourceIndex];
        if (!source) continue;
        for (var pathIndex = 0; pathIndex < paths.length; pathIndex++) {
            var value = getValueAtPath(source, paths[pathIndex]);
            if (hasValue(value)) {
                return value;
            }
        }
    }
    return null;
}

function isUnlimitedValue(value) {
    if (value === Infinity) return true;
    if (!hasValue(value)) return false;
    if (typeof value === "string") {
        var normalizedValue = value.trim().toLowerCase();
        return normalizedValue === "unlimited" ||
            normalizedValue === "infinity" ||
            normalizedValue === "unlimited reviewers";
    }
    var numericValue = toNumber(value);
    return numericValue !== null && numericValue < 0;
}

function formatNumberForDisplay(value) {
    var numericValue = roundToOneDecimal(value);
    if (numericValue === null) return null;
    if (Math.abs(numericValue - Math.round(numericValue)) < 0.000001) {
        return String(Math.round(numericValue));
    }
    return String(numericValue);
}

function formatHoursForDisplay(value) {
    return formatNumberForDisplay(value);
}

function formatGigabytesForDisplay(value) {
    var formattedValue = formatNumberForDisplay(value);
    return formattedValue !== null ? formattedValue + " GB" : null;
}

function formatCurrencyForDisplay(value) {
    if (!hasValue(value)) return null;
    if (typeof value === "string") return value;
    var numericValue = toNumber(value);
    return numericValue !== null ? "$" + numericValue.toFixed(2) : null;
}

function normalizeEmail(value) {
    return hasValue(value) ? String(value).trim().toLowerCase() : null;
}

function normalizeIdentifier(value) {
    return hasValue(value) ? String(value) : null;
}

function getNormalizedIdentifierValues(source, paths) {
    var identifiers = [];
    for (var i = 0; i < paths.length; i++) {
        var normalizedValue = normalizeIdentifier(getValueAtPath(source, paths[i]));
        if (normalizedValue && identifiers.indexOf(normalizedValue) === -1) {
            identifiers.push(normalizedValue);
        }
    }
    return identifiers;
}

function memberConsumesEditorSeat(member) {
    return !!member &&
        member.status !== "invited" &&
        member.status !== "pending" &&
        member.role !== "viewer" &&
        member.role !== "reviewer";
}

function countSeatConsumingMembers(members) {
    if (!Array.isArray(members)) return 0;
    return members.filter(memberConsumesEditorSeat).length;
}

function memberReservesPendingEditorSeat(member) {
    return !!member &&
        (member.status === "invited" || member.status === "pending") &&
        member.role !== "viewer" &&
        member.role !== "reviewer";
}

function countPendingEditorSeatInvites(members) {
    if (!Array.isArray(members)) return 0;
    return members.filter(memberReservesPendingEditorSeat).length;
}

function priceValueHasExplicitPeriod(value) {
    if (typeof value !== "string") return false;
    return /(?:\/\s*(?:mo|month|yr|year)|per\s+(?:month|year)|monthly|annual|annually|billed)/i.test(value);
}

function titleCase(value) {
    if (!hasValue(value)) return null;
    return String(value)
        .replace(/[_-]+/g, " ")
        .replace(/\b\w/g, function (character) {
            return character.toUpperCase();
        });
}

// Extract a user-facing message from a failed API call, appending the Stripe
// error code when present. Keeps debugging info inline on the Settings page
// rather than in a generic "Something went wrong" modal - the motivating bug
// here was a generic modal that hid the underlying Stripe error.
function _resolveStripeErrorMessage(error, fallbackMessage) {
    if (!error) return fallbackMessage;
    var baseMessage = null;
    if (error.responseJSON) {
        baseMessage = error.responseJSON.message
            || error.responseJSON.error
            || error.responseJSON.errorMessage;
    }
    if (!baseMessage && typeof error.message === "string" && error.message.length > 0) {
        baseMessage = error.message;
    }
    if (!baseMessage) baseMessage = fallbackMessage;
    var stripeCode = error.responseJSON ? (error.responseJSON.errorCode || error.responseJSON.code) : null;
    if (stripeCode) {
        return baseMessage + " (" + stripeCode + ")";
    }
    return baseMessage;
}

function normalizePlanKey(planType) {
    if (!hasValue(planType)) return null;
    var normalizedValue = String(planType).trim().toLowerCase().replace(/[\s_-]+/g, "");
    if (normalizedValue.indexOf("free") === 0) return "free";
    if (normalizedValue.indexOf("starter") === 0) return "starter";
    if (normalizedValue.indexOf("team") === 0) return "team";
    if (normalizedValue === "proplus" || normalizedValue.indexOf("pro") === 0) return "pro";
    if (normalizedValue.indexOf("enterprise") === 0) return "enterprise";
    if (
        normalizedValue.indexOf("business") === 0 ||
        normalizedValue.indexOf("custom") === 0 ||
        normalizedValue.indexOf("contract") === 0
    ) {
        return "custom";
    }
    return normalizedValue;
}

const SettingsPage2Controller = Chaplin.Controller.extend({

    initialize: function (options) {
        _.extend(this, Backbone.Events);
        this._pageId = options ? options.pageId : null;
        this.listenTo(
            LocalStorageHelper.getInstance(),
            LocalStorageHelper.getInstance().eventTypes.USER_INFO_UPDATED,
            this.onUserInfoChanged.bind(this)
        );
        var userInfoJson = LocalStorageHelper.getInstance().getUserInfo();
        this._userInfo = userInfoJson ? Utils.getInstance().getModelFromUserJson(userInfoJson) : null;
        this._workspace = null;
        this._userRole = null;
        this._members = [];
        this._reviewers = [];
        this._reviewerLoadPromise = null;
        this._workspaceLoadVersion = 0;

        // Refresh workspace state whenever any flow (upgrade popup, plan
        // change, billing portal return) signals that the underlying
        // workspace doc has changed. The upgrade-to-team popup persists the
        // new context to LocalStorage and dispatches this event, but it
        // doesn't otherwise re-render the Settings page beneath it — so
        // without this listener the billing panel keeps showing the old
        // planType / $0 price until the user hard-refreshes.
        this._onWorkspaceLibraryUpdated = this._handleWorkspaceLibraryUpdated.bind(this);
        if (typeof window !== "undefined" && window.addEventListener) {
            window.addEventListener("trebble:workspace-library-updated", this._onWorkspaceLibraryUpdated);
        }
    },

    _handleWorkspaceLibraryUpdated: function () {
        if (!WORKSPACE_ENABLED || !USE_REAL_WORKSPACE_DATA) {
            return;
        }
        // Bypass the workspaceId-equality short-circuit in
        // onUserInfoChanged: the in-place upgrade keeps the same
        // workspaceId, only the plan/price changes — so we must always
        // re-fetch the workspace details from the API rather than
        // comparing IDs.
        this._loadWorkspaceData();
    },

    dispose: function () {
        this._cancelWorkspaceReviewersLoad();
        if (this._pendingHourPackRefreshTimeoutId) {
            clearTimeout(this._pendingHourPackRefreshTimeoutId);
            this._pendingHourPackRefreshTimeoutId = null;
        }
        if (typeof window !== "undefined" && window.removeEventListener && this._onWorkspaceLibraryUpdated) {
            window.removeEventListener("trebble:workspace-library-updated", this._onWorkspaceLibraryUpdated);
            this._onWorkspaceLibraryUpdated = null;
        }
        return Chaplin.Controller.prototype.dispose.apply(this, arguments);
    },

    _cancelWorkspaceReviewersLoad: function () {
        if (this._reviewerLoadPromise && typeof this._reviewerLoadPromise.cancel === "function") {
            this._reviewerLoadPromise.cancel();
        }
        this._reviewerLoadPromise = null;
    },

    onUserInfoChanged: function (newUserInfoJson) {
        this._userInfo = newUserInfoJson ? Utils.getInstance().getModelFromUserJson(newUserInfoJson) : null;
        if (this.view) {
            this.view.updateUserInfo(this._userInfo);
        }
        if (!WORKSPACE_ENABLED || !USE_REAL_WORKSPACE_DATA) {
            return;
        }

        var currentWorkspaceId = LocalStorageHelper.getInstance().getCurrentWorkspaceId();
        var loadedWorkspaceId = this._workspace ? this._workspace.workspaceId : null;
        if (currentWorkspaceId !== loadedWorkspaceId) {
            this._loadWorkspaceData();
        }
    },

    getModel: function () {
        return this.model;
    },

    getView: function () {
        return this.view;
    },

    editProfile: function () {
        PersistentModels.getInstance().getRouter().showUserEditProfileInfo(true);
    },

    changePassword: function () {
        PersistentModels.getInstance().getRouter().showUserChangePasswordPage(true);
    },

    changeProfilePicture: function () {
        PersistentModels.getInstance().getRouter().changeProfileImage();
    },

    logout: function () {
        PersistentModels.getInstance().getRouter().logOut();
    },

    togglePushNotifications: function (enabled) {
        if (enabled) {
            PushNotificationHelper.getInstance().initNativePushNotification();
        } else {
            PushNotificationHelper.getInstance().unregisterNativePushNotification();
        }
    },

    toggleEmailNotifications: function (enabled) {
        var updatePromise = enabled
            ? TrebbleClientAPIHelper.getInstance().enableEmailDigestNotification()
            : TrebbleClientAPIHelper.getInstance().disableEmailDigestNotification();
        updatePromise.then((function () {
            var username = LocalStorageHelper.getInstance().getUserInfo().username;
            return TrebbleClientAPIHelper.getInstance().getUserProfileInfoByUsername(username);
        }).bind(this)).then(function (updatedUserInfo) {
            LocalStorageHelper.getInstance().updateUserInfo(updatedUserInfo);
        }).catch(function (error) {
            console.error("Error updating email notification setting:", error);
        });
    },

    inviteMember: function () {
        // Legacy Stripe creator-sub users have `workspace.workspaceId === null`
        // (synthesized default workspace). The previous early-bail here meant
        // their Invite click did nothing. The backend now materializes a
        // Workspace row in place on the first invite request, so we let the
        // request through with a null workspaceId. The success handler below
        // captures any newly-minted workspaceId from the response so the
        // refresh reads from the right doc.
        var self = this;
        var initialWorkspaceId = this._workspace ? this._workspace.workspaceId : null;
        PersistentModels.getInstance().getRouter().showWorkspaceMembersPopup(this, initialWorkspaceId, {
            mode: "invite",
            allowInviteModeBypass: true,
            onInviteSuccess: function (response) {
                // Backend response shape:
                //   { workspaceId, workspaceMaterialized?, workspacePlanType?, ... }
                // If a workspace was just materialized, update local state
                // before the refresh so subsequent calls hit the new id.
                var newWorkspaceId = (response && response.workspaceId) || initialWorkspaceId;
                if (response && response.workspaceMaterialized && newWorkspaceId) {
                    if (self._workspace) {
                        self._workspace.workspaceId = newWorkspaceId;
                        self._workspace.isPersonal = false;
                    }
                    if (window.alertSuccessMessage) {
                        window.alertSuccessMessage("Workspace created — invitation sent");
                    }
                }
                // Persist the (possibly newly materialized) workspaceId in
                // LocalStorage BEFORE _refreshWorkspaceData. _loadWorkspaceDetails
                // bails via _isWorkspaceLoadCurrent if LocalStorageHelper still
                // reports a different currentWorkspaceId (null in the
                // synthesized-default case), which would otherwise leave the
                // members list silently un-loaded after the first invite.
                if (newWorkspaceId) {
                    LocalStorageHelper.getInstance().setCurrentWorkspaceId(newWorkspaceId);
                }
                return self._refreshWorkspaceData(newWorkspaceId);
            }
        });
    },

    removeMember: function (memberRecordId) {
        var workspaceId = this._workspace ? this._workspace.workspaceId : null;
        if (!workspaceId || !memberRecordId) return;
        var self = this;
        var confirmTitle = "Remove team member?";
        var confirmBody = "This person will immediately lose access to all workspace projects.";
        Utils.getInstance().showUserConfirmationDialog(confirmTitle, confirmBody).then(function(confirmed) {
            if (confirmed !== true) return;
            return TrebbleClientAPIHelper.getInstance().removeWorkspaceMember({
                workspaceId: workspaceId,
                memberRecordId: memberRecordId
            }).then(function() {
                window.alertSuccessMessage("Team member removed");
                return self._refreshWorkspaceData(workspaceId);
            }).catch(function(error) {
                console.error("Error removing workspace member:", error);
                window.alertErrorMessage("Could not remove member");
            });
        }).catch(function(e) {
            console.warn("Confirmation dialog error:", e);
        });
    },

    removeReviewer: function (reviewer) {
        var workspaceId = this._workspace ? this._workspace.workspaceId : null;
        if (!workspaceId || !reviewer) return;
        var reviewerEmail = reviewer.email || null;
        var reviewerUserUID = reviewer.userUID || null;
        if (!reviewerEmail && !reviewerUserUID) {
            window.alertErrorMessage("Could not remove reviewer");
            return;
        }
        var reviewerName = reviewer.name || reviewer.email || "this reviewer";
        var self = this;
        var confirmTitle = "Remove reviewer?";
        var confirmBody = reviewerName + " will lose access to all projects shared with them in this workspace.";
        Utils.getInstance().showUserConfirmationDialog(confirmTitle, confirmBody).then(function(confirmed) {
            if (confirmed !== true) return;
            return TrebbleClientAPIHelper.getInstance().revokeWorkspaceReviewer({
                workspaceId: workspaceId,
                email: reviewerEmail,
                userUID: reviewerUserUID
            }).then(function() {
                window.alertSuccessMessage("Reviewer access removed");
                return self._refreshWorkspaceData(workspaceId);
            }).catch(function(error) {
                console.error("Error removing workspace reviewer:", error);
                window.alertErrorMessage("Could not remove reviewer");
            });
        }).catch(function(e) {
            console.warn("Confirmation dialog error:", e);
        });
    },

    leaveWorkspace: function () {
        var workspaceId = this._workspace ? this._workspace.workspaceId : null;
        var workspaceName = this._workspace ? this._workspace.workspaceName : "this workspace";
        if (!workspaceId) return;
        var confirmTitle = "Leave " + workspaceName + "?";
        var confirmBody = "Your projects will be transferred to the workspace owner and you will lose access.";
        Utils.getInstance().showUserConfirmationDialog(confirmTitle, confirmBody).then(function(confirmed) {
            if (confirmed !== true) return;
            return TrebbleClientAPIHelper.getInstance().leaveWorkspace({
                workspaceId: workspaceId
            }).then(function() {
                LocalStorageHelper.getInstance().setCurrentWorkspaceId(null);
                LocalStorageHelper.getInstance().setWorkspaceContext(null);
                window.alertSuccessMessage("You left " + workspaceName);
                var baseUrl = (window.location.protocol == "http:" || window.location.protocol == "https:") ? location.origin + window.getAppBaseUrl() : window.getBaseUrlWithoutEndSlash();
                return TrebbleClientAPIHelper.getInstance().getMyWorkspaces().then(function(workspaces) {
                    LocalStorageHelper.getInstance().setWorkspaceList(workspaces || []);
                    if (typeof window !== "undefined" && window.dispatchEvent) {
                        window.dispatchEvent(new CustomEvent("trebble:workspace-library-updated"));
                    }
                }).catch(function(error) {
                    console.error("Error refreshing workspace list after leave:", error);
                }).then(function() {
                    // Full reload to ensure home menu and workspace context are refreshed
                    window.location.replace(baseUrl + "#projects");
                    window.location.reload();
                });
            }).catch(function(error) {
                console.error("Error leaving workspace:", error);
                window.alertErrorMessage(error.message || "Could not leave workspace");
            });
        }).catch(function(e) {
            console.warn("Confirmation dialog error:", e);
        });
    },

    deleteWorkspace: function () {
        var workspaceId = this._workspace ? this._workspace.workspaceId : null;
        if (!workspaceId) return;
        // The confirmation (type workspace name) is handled in the React component.
        // This method is called only after the user has typed the correct name and clicked confirm.
        TrebbleClientAPIHelper.getInstance().deleteWorkspace({
            workspaceId: workspaceId
        }).then(function() {
            LocalStorageHelper.getInstance().setCurrentWorkspaceId(null);
            LocalStorageHelper.getInstance().setWorkspaceContext(null);
            window.alertSuccessMessage(window.getI18n(ti18n, "DELETE_WORKSPACE_SUCCESS") || "Workspace deleted successfully.");
            var baseUrl = (window.location.protocol == "http:" || window.location.protocol == "https:") ? location.origin + window.getAppBaseUrl() : window.getBaseUrlWithoutEndSlash();
            return TrebbleClientAPIHelper.getInstance().getMyWorkspaces().then(function(workspaces) {
                LocalStorageHelper.getInstance().setWorkspaceList(workspaces || []);
                if (typeof window !== "undefined" && window.dispatchEvent) {
                    window.dispatchEvent(new CustomEvent("trebble:workspace-library-updated"));
                }
            }).catch(function(error) {
                console.error("Error refreshing workspace list after delete:", error);
            }).then(function() {
                window.location.replace(baseUrl + "#projects");
                window.location.reload();
            });
        }).catch(function(error) {
            console.error("Error deleting workspace:", error);
            window.alertErrorMessage(window.getI18n(ti18n, "DELETE_WORKSPACE_FAILED") || "Could not delete workspace.");
        });
    },

    setMemberRole: function (memberRecordId, newRole) {
        var workspaceId = this._workspace ? this._workspace.workspaceId : null;
        if (!workspaceId || !memberRecordId) return;
        var self = this;
        TrebbleClientAPIHelper.getInstance().setWorkspaceMemberRole({
            workspaceId: workspaceId,
            memberRecordId: memberRecordId,
            role: newRole
        }).then(function() {
            window.alertSuccessMessage("Role updated");
            return self._refreshWorkspaceData(workspaceId);
        }).catch(function(error) {
            console.error("Error updating member role:", error);
            window.alertErrorMessage("Could not update role");
        });
    },

    upgradePlan: function () {
        // Single source of truth for both the synthesized default-workspace
        // path AND real-workspace owners. Both should land in the unified
        // upgrade flow (Starter / Pro / Team) — the legacy creator-sub
        // popup we used to call for `isPersonal` only surfaced Starter/Pro
        // and didn't include Team, which is the discoverability gap users
        // hit at `#settings2` from the synthesized default workspace.
        //
        // shouldShowUpgradeCTA / getRecommendedUpgradeMode read the user's
        // owned-workspace state via LocalStorageHelper, not the controller's
        // `_workspace` view-model, so they correctly resolve for both
        // contexts:
        //   - synthesized default workspace + free user → 'create' (the
        //     popup will lazy-materialize a Workspace doc on success)
        //   - user already owns a Free workspace → 'upgrade_in_place' (the
        //     existing Free row transforms to the chosen paid tier)
        //   - user already owns a paid workspace → button is hidden
        if (!shouldShowUpgradeCTA()) {
            return;
        }
        var mode = getRecommendedUpgradeMode();
        PersistentModels.getInstance().getRouter().showUpgradeToTeamPopup(this, { mode: mode });
    },

    updatePaymentMethod: function () {
        var isPersonal = !!(this._workspace && this._workspace.isPersonal);
        if (isPersonal) {
            // Default workspace → legacy creator-sub Stripe Customer Portal
            // (or Checkout setup if no card yet).
            return this._openCreatorBillingPortal();
        }
        var workspaceId = this._workspace ? this._workspace.workspaceId : null;
        if (!workspaceId) return;
        // Route by the real billing state, not by the button label. The
        // legacy code sent every caller through the Customer Portal, but
        // Portal requires an existing customer with a default source -
        // workspaces on Free/Starter that never completed a paid upgrade
        // have neither, and Stripe throws (the "Could not open billing
        // portal" modal this fix addresses). Checkout in setup-mode is the
        // canonical flow for attaching a first payment method.
        var plan = (this._workspace && this._workspace.plan) || {};
        if (plan.hasPaymentMethod === true) {
            return this._openBillingPortal(workspaceId);
        }
        return this.addPaymentMethod();
    },

    _openCreatorBillingPortal: function () {
        var returnUrl = this._getWorkspaceBillingReturnUrl();
        return TrebbleClientAPIHelper.getInstance()
            .getStripeCreatorCustomerPortalUrl(returnUrl, true, false)
            .then(function (response) {
                var portalUrl = response && (response.url || response.portalUrl || response);
                if (typeof portalUrl === "string" && portalUrl.length > 0) {
                    window.location.href = portalUrl;
                } else {
                    window.alertErrorMessage("Could not open billing portal");
                }
            }).catch(function (error) {
                console.error("Error opening creator-sub billing portal:", error);
                window.alertErrorMessage(_resolveStripeErrorMessage(error, "Could not open billing portal"));
            });
    },

    _getWorkspaceBillingReturnUrl: function () {
        if (typeof window === "undefined" || !window.location) {
            return null;
        }
        var baseUrl = (window.location.protocol === "http:" || window.location.protocol === "https:")
            ? location.origin + window.getAppBaseUrl()
            : window.getBaseUrlWithoutEndSlash();
        return baseUrl + "#settings";
    },

    addPaymentMethod: function () {
        var workspaceId = this._workspace ? this._workspace.workspaceId : null;
        if (!workspaceId) return;
        // Stripe rejects file:// return URLs, so normalize every environment
        // back to the canonical Settings route.
        var returnUrl = this._getWorkspaceBillingReturnUrl();
        TrebbleClientAPIHelper.getInstance().createWorkspaceSetupCheckoutSession({
            workspaceId: workspaceId,
            successUrl: returnUrl,
            cancelUrl: returnUrl
        }).then(function (response) {
            var url = response && (response.url || (response.session && response.session.url));
            if (url) {
                window.location.href = url;
            } else {
                window.alertErrorMessage("Could not start payment setup");
            }
        }).catch(function (error) {
            console.error("Error creating workspace payment setup session:", error);
            window.alertErrorMessage(_resolveStripeErrorMessage(error, "Could not start payment setup"));
        });
    },

    _openBillingPortal: function (workspaceId) {
        var returnUrl = this._getWorkspaceBillingReturnUrl();
        return TrebbleClientAPIHelper.getInstance().getWorkspaceBillingPortalUrl({
            workspaceId: workspaceId,
            returnUrl: returnUrl
        }).then(function (response) {
            var portalUrl = response && (response.url || response.portalUrl);
            if (portalUrl) {
                window.location.href = portalUrl;
            } else {
                window.alertErrorMessage("Could not open billing portal");
            }
        }).catch(function (error) {
            console.error("Error opening workspace billing portal:", error);
            window.alertErrorMessage(_resolveStripeErrorMessage(error, "Could not open billing portal"));
        });
    },

    changePlan: function () {
        var isPersonal = !!(this._workspace && this._workspace.isPersonal);
        var hasStripeSub = !!(this._workspace && this._workspace.plan && this._workspace.plan.hasStripeSubscription);

        if (isPersonal && hasStripeSub) {
            // Personal workspace WITH an active Stripe creator subscription.
            // The user has a recurring charge attached to their User doc
            // (stripeCreatorSubscriptionInfo) — we need a flow that knows
            // how to MODIFY that existing sub (Stripe Customer Portal).
            // The new UpgradeToTeam modal would create a new workspace +
            // sub and orphan the existing one, so we keep the legacy popup
            // here. TODO: build a unified "change creator-sub tier" flow
            // that includes Team as a destination.
            return PersistentModels.getInstance().getRouter().showUpgradeCreatorSubscriptionPopup();
        }

        if (isPersonal) {
            // Personal context, no Stripe sub. Could be:
            //   - Lifetime AppSumo (planType=custom, isLifetime=true) — they
            //     can stack Team on top via the new modal (creates a new
            //     workspace; the AppSumo credit stays on the personal default).
            //   - Promo / gift credit (planType=custom, isLifetime=false) —
            //     same: route through new modal so they see Starter/Pro/Team.
            //   - Free user who somehow ended up on the Change-Plan button
            //     (shouldn't happen since the button label is Upgrade for
            //     planType=free, but defensive routing).
            // No need to gate on shouldShowUpgradeCTA here: the user
            // explicitly clicked Change Plan, intent is clear.
            var personalMode = getRecommendedUpgradeMode();
            return PersistentModels.getInstance().getRouter().showUpgradeToTeamPopup(this, { mode: personalMode });
        }

        var workspaceId = this._workspace ? this._workspace.workspaceId : null;
        if (!workspaceId) return;
        var currentPlanType = (this._workspace && this._workspace.plan && this._workspace.plan.planType) || 'free';
        var currentBillingCycle = (this._workspace && this._workspace.plan && this._workspace.plan.billingCycle) || 'monthly';
        var workspaceUsage = this._workspaceUsage || {};
        PersistentModels.getInstance().getRouter().showChangePlanPopup(this, {
            workspaceId: workspaceId,
            currentPlanType: currentPlanType,
            currentBillingCycle: currentBillingCycle,
            workspaceUsage: workspaceUsage,
            onSuccess: this._refreshWorkspaceData.bind(this, workspaceId)
        });
    },

    cancelSubscription: function () {
        var isPersonal = !!(this._workspace && this._workspace.isPersonal);
        var self = this;
        var confirmTitle = "Cancel subscription?";
        var confirmBody = isPersonal
            ? "Your subscription will end at the end of the current billing period. You will keep your current plan features until then."
            : "Your workspace will be downgraded to the Free plan at the end of the current billing period. You will keep your current plan features until then.";
        Utils.getInstance().showUserConfirmationDialog(confirmTitle, confirmBody).then(function (confirmed) {
            if (confirmed !== true) return;
            if (isPersonal) {
                // Default workspace → legacy /cancelCreatorSubscription endpoint.
                return SubscriptionManager.getInstance().cancelCreatorSubscription().then(function () {
                    window.alertSuccessMessage("Your subscription has been cancelled. You will be downgraded at the end of the current billing period.");
                    return self._loadDefaultWorkspaceData();
                }).catch(function (error) {
                    console.error("Error cancelling creator subscription:", error);
                    window.alertErrorMessage(error && error.message ? error.message : "Could not cancel subscription");
                });
            }
            var workspaceId = self._workspace ? self._workspace.workspaceId : null;
            if (!workspaceId) return;
            TrebbleClientAPIHelper.getInstance().cancelWorkspaceSubscription({
                workspaceId: workspaceId
            }).then(function () {
                window.alertSuccessMessage("Your subscription has been cancelled. You will be downgraded at the end of the current billing period.");
                return self._refreshWorkspaceData(workspaceId);
            }).catch(function (error) {
                console.error("Error cancelling workspace subscription:", error);
                window.alertErrorMessage(error && error.message ? error.message : "Could not cancel subscription");
            });
        }).catch(function (e) {
            console.warn("Confirmation dialog error:", e);
        });
    },

    purchaseHourPack: function () {
        var workspaceId = this._workspace ? this._workspace.workspaceId : null;
        if (!workspaceId) return;
        var workspaceUsage = this._workspaceUsage || {};
        // Listen once for the purchase completion event so we can refresh usage.
        // For SCA (3DS) flows the backend credits hours asynchronously via
        // webhook, so the immediate refresh will normally see stale totals —
        // schedule a follow-up refresh a few seconds later so Settings > Usage
        // converges without the user having to reload.
        this.off('hourPackPurchased');
        this.once('hourPackPurchased', (function (eventPayload) {
            this._refreshWorkspaceData(workspaceId);
            if (eventPayload && eventPayload.isAsync) {
                if (this._pendingHourPackRefreshTimeoutId) {
                    clearTimeout(this._pendingHourPackRefreshTimeoutId);
                }
                this._pendingHourPackRefreshTimeoutId = setTimeout((function () {
                    this._pendingHourPackRefreshTimeoutId = null;
                    this._refreshWorkspaceData(workspaceId);
                }).bind(this), 5000);
            }
        }).bind(this));
        PersistentModels.getInstance().getRouter().showHourPackPopup(this, {
            workspaceId: workspaceId,
            workspaceUsage: workspaceUsage
        });
    },

    showHomePage: function () {
        PersistentModels.getInstance().getRouter().showHomePage();
    },

    onPageShow: function () {
        if (WORKSPACE_ENABLED && USE_REAL_WORKSPACE_DATA) {
            this._loadWorkspaceData();
        }
    },

    _beginWorkspaceLoad: function () {
        this._workspaceLoadVersion += 1;
        return this._workspaceLoadVersion;
    },

    _isWorkspaceLoadCurrent: function (workspaceId, loadVersion) {
        return this._workspaceLoadVersion === loadVersion &&
            LocalStorageHelper.getInstance().getCurrentWorkspaceId() === workspaceId;
    },

    _isDefaultPersonalWorkspaceSummary: function (workspace) {
        return !!(workspace &&
            (workspace.isDefaultPersonalWorkspace === true || workspace.isDefaultWorkspace === true) &&
            workspace.myRole === "owner");
    },

    _getWorkspaceSummaryForId: function (workspaceId) {
        if (!workspaceId) {
            return null;
        }
        var localStorageHelper = LocalStorageHelper.getInstance();
        var context = localStorageHelper.getWorkspaceContext();
        var workspaceList = [];
        try {
            workspaceList = localStorageHelper.getWorkspaceList() || [];
        } catch (error) {
            workspaceList = [];
        }
        for (var i = 0; i < workspaceList.length; i++) {
            if (workspaceList[i] && workspaceList[i].workspaceId === workspaceId) {
                return context && context.workspaceId === workspaceId
                    ? Object.assign({}, context, workspaceList[i])
                    : workspaceList[i];
            }
        }
        if (context && context.workspaceId === workspaceId) {
            return context;
        }
        return null;
    },

    _isDefaultPersonalWorkspaceId: function (workspaceId) {
        return this._isDefaultPersonalWorkspaceSummary(this._getWorkspaceSummaryForId(workspaceId));
    },

    _shouldUseSynthesizedDefaultWorkspace: function (workspaceId) {
        if (!workspaceId) {
            return true;
        }

        var workspaceSummary = this._getWorkspaceSummaryForId(workspaceId);
        if (!this._isDefaultPersonalWorkspaceSummary(workspaceSummary)) {
            return false;
        }

        var planType = normalizePlanKey(
            getFirstValueFromSources([workspaceSummary], ["planType", "plan.planType"])
        );
        var hasWorkspaceStripeSubscription = getFirstValueFromSources(
            [workspaceSummary],
            ["hasStripeSubscription", "billing.hasStripeSubscription", "plan.hasStripeSubscription"]
        ) === true;

        // Default-personal rows without a workspace Stripe subscription are
        // still legacy Personal Space/AppSumo/creator-sub billing, even when
        // planType looks paid (starter/pro). Calling workspace changePlan on
        // those rows fails because there is no subscription to mutate.
        return hasWorkspaceStripeSubscription !== true;
    },

    _clearWorkspaceState: function () {
        this._beginWorkspaceLoad();
        this._workspace = null;
        this._userRole = null;
        this._members = [];
        this._updateView();
        return RSVP.Promise.resolve();
    },

    /**
     * Build the synthesized "default workspace" view-model. Used when the user
     * has no explicit workspaceId — i.e. either a brand-new free user or a
     * legacy account on the Stripe creator-subscription flow (Trebble Starter /
     * Trebble Pro) plus optional AppSumo lifetime credit.
     *
     * The default workspace is conceptually a workspace; it just doesn't have
     * a Workspace doc in Mongo until the user upgrades to a paid Workspace
     * plan or otherwise materializes it. The settings UI treats it the same
     * as a real workspace, just with `isPersonal: true` flagging the cases
     * where workspace-only actions (members, leave, delete) don't apply.
     */
    _loadDefaultWorkspaceData: function () {
        var loadVersion = this._beginWorkspaceLoad();
        var TrebbleAPI = TrebbleClientAPIHelper.getInstance();
        return RSVP.hash({
            stripeSubscription: SubscriptionManager.getInstance()
                .getTrebbeCreatorSubscriptionUserIsSubscribedTo(true)
                .catch(function () { return null; }),
            transcriptionInfo: TrebbleAPI.getNumberOfMinutesOfTranscriptionLeft()
                .catch(function () { return null; }),
            // Personal-scope storage usage. Backed by GET/POST
            // /api/v1/storage/usage. Returns 503 when the StorageService
            // env flag is off — we tolerate that and just leave the
            // storage UI showing "--" rather than failing the whole load.
            // Errors and missing data both resolve to null so the
            // builder treats them uniformly as "unknown usage".
            storageUsage: TrebbleAPI.getMyStorageUsage()
                .catch(function () { return null; })
        }).then((function (results) {
            // Same staleness check we use for real workspaces — bail if a
            // newer load has started or the user switched workspace contexts.
            if (this._workspaceLoadVersion !== loadVersion) return null;
            var currentWorkspaceId = LocalStorageHelper.getInstance().getCurrentWorkspaceId();
            if (!this._shouldUseSynthesizedDefaultWorkspace(currentWorkspaceId)) return null;
            this._workspace = this._buildDefaultWorkspaceViewModel(
                results.stripeSubscription,
                results.transcriptionInfo,
                results.storageUsage
            );
            this._userRole = "owner"; // user owns their own default workspace
            this._members = this._buildCurrentUserOwnerMemberList();
            this._updateView();
            return this._workspace;
        }).bind(this)).catch(function (error) {
            console.error("Error loading default-workspace data:", error);
            return null;
        });
    },

    _buildCurrentUserOwnerMemberList: function () {
        if (!this._userInfo) {
            return [];
        }
        var userId = this._userInfo.getUserId ? this._userInfo.getUserId() : null;
        var fullName = this._userInfo.getFullName ? this._userInfo.getFullName() : null;
        var username = this._userInfo.getUsername ? this._userInfo.getUsername() : null;
        var email = this._userInfo.getEmail ? this._userInfo.getEmail() : null;
        var profileImageUrl = this._userInfo.getProfileImageUrl ? this._userInfo.getProfileImageUrl() : null;

        return [{
            userId: userId,
            memberRecordId: null,
            name: fullName || username || email || "You",
            email: email,
            role: "owner",
            status: "active",
            profileImageUrl: profileImageUrl && profileImageUrl !== "none" ? profileImageUrl : null,
            isYou: true,
            hoursUsed: null,
            storageUsedGB: null,
        }];
    },

    _buildDefaultWorkspaceViewModel: function (stripeSubscription, transcriptionInfo, storageUsage) {
        var info = transcriptionInfo || {};
        var hasStripeSub = !!stripeSubscription;
        var isLifetime = !!info.isLifetime;
        var tierEquivalent = info.tierEquivalent || null;

        // Plan label rules:
        //   stripe-only Starter      → "Trebble Starter"
        //   stripe-only Pro          → "Trebble Pro"
        //   stripe-only Pro Plus     → "Trebble Pro Plus"
        //   any AppSumo lifetime     → "Custom" (alone OR layered on a Stripe sub)
        //   neither                  → "Free"
        // Lifetime users are intentionally NOT labeled with a paid-tier name —
        // see the isLifetime branch below for the rationale.
        var stripeTierName = null;
        var stripePlanType = null;
        var stripePrice = null;
        var stripePricePeriod = null;
        var billedYearly = false;
        if (hasStripeSub) {
            // CREATOR_PRO_*       → user-facing "Starter"
            // CREATOR_PRO_PLUS_*  → user-facing "Pro"
            // (See Utils.getCreatorFirstPaidTierName / SecondPaidTierName.)
            if (StripeHelper.getInstance().isSubscriptionActiveForCreatorProPlus(stripeSubscription)) {
                stripeTierName = Utils.getInstance().getCreatorSecondPaidTierName();
                stripePlanType = "pro";
                billedYearly = StripeHelper.getInstance().isSubscriptionActiveForCreatorProPlusYearly(stripeSubscription);
                var proPlusMonthly = Utils.getInstance().getCreatorSecondPaidTierCostInUSDPerMonth(billedYearly);
                stripePrice = "$" + proPlusMonthly;
                stripePricePeriod = "/mo" + (billedYearly ? " billed yearly" : "");
            } else if (StripeHelper.getInstance().isSubscriptionActiveForCreatorPro(stripeSubscription)) {
                stripeTierName = Utils.getInstance().getCreatorFirstPaidTierName();
                stripePlanType = "starter";
                billedYearly = StripeHelper.getInstance().isSubscriptionActiveForCreatorProYearly(stripeSubscription);
                var proMonthly = Utils.getInstance().getCreatorFirstPaidTierCostInUSDPerMonth(billedYearly);
                stripePrice = "$" + proMonthly;
                stripePricePeriod = "/mo" + (billedYearly ? " billed yearly" : "");
            }
        }

        var totalMinutes = toNumber(info.totalMinutesAvailablePerMonth);
        var hoursPerMonth = totalMinutes !== null ? Math.round((totalMinutes / 60) * 10) / 10 : null;
        var usagePeriodStart = getFirstValueFromSources(
            [info],
            ["periodStart", "currentPeriodStart", "cycleStartDate", "latestBillingCycleDateStart"]
        );
        var usagePeriodEnd = getFirstValueFromSources(
            [info],
            ["periodEnd", "currentPeriodEnd", "cycleEndDate", "latestBillingCycleDateEnd"]
        );
        if (!usagePeriodStart && stripeSubscription && stripeSubscription.current_period_start) {
            usagePeriodStart = new Date(stripeSubscription.current_period_start * 1000).toISOString();
        }
        if (!usagePeriodEnd && stripeSubscription && stripeSubscription.current_period_end) {
            usagePeriodEnd = new Date(stripeSubscription.current_period_end * 1000).toISOString();
        }

        // Detect "Free user with bonus minutes" — a user with no Stripe sub and
        // no AppSumo lifetime, but a non-default totalMinutesAvailablePerMonth.
        // The bump comes from non-lifetime redeem codes (promo, gift, partner
        // credit). They're NOT on the standard Free tier (its quota is
        // FALLBACK_PLAN_FEATURES.free.hours * 60 = 30 min), so labeling them
        // "Free" misleads — they have 5.7 hr / 12 hr / whatever the redeem
        // codes summed to, not the default 0.5 hr/month.
        //
        // We mirror the catalog's free-tier minutes here (instead of
        // hardcoding 30) so any future change to FALLBACK_PLAN_FEATURES.free
        // automatically flows through. We use null-coalescing rather than
        // `||` so a deliberate `free.hours = 0` (a hypothetical future
        // "no free tier" config) is preserved instead of being treated as
        // missing and defaulted to 0.5.
        var freeHoursFromCatalog = WorkspacePricingCatalog.FALLBACK_PLAN_FEATURES.free
            && WorkspacePricingCatalog.FALLBACK_PLAN_FEATURES.free.hours;
        var freeTierMinutes = (freeHoursFromCatalog == null ? 0.5 : freeHoursFromCatalog) * 60;
        var hasNonDefaultMinutesWithoutPaidPlan = !isLifetime
            && !hasStripeSub
            && totalMinutes !== null
            && totalMinutes > freeTierMinutes;

        var planType, planName, planPrice, planPricePeriod;
        if (isLifetime) {
            // Any AppSumo lifetime credit (alone OR layered on a Stripe sub) →
            // "Custom". We deliberately DO NOT call this "Trebble Pro (Lifetime)"
            // or "Trebble Starter (Lifetime)" — AppSumo deals were sold as a
            // bundle of transcription minutes, not as a perpetual entitlement to
            // every feature shipped under the Pro / Starter tier names. Labeling
            // a lifetime customer with a tier name implies feature parity with
            // current paying customers (which may diverge over time) and creates
            // unnecessary support / liability risk. The plan-card subtitle and
            // the AppSumo disclaimer in WsBillingPanel still surface that hours
            // are lifetime and don't expire.
            planType = "custom";
            planName = "Custom";
            // If the user ALSO has a Stripe sub, surface the stripe recurring
            // charge as the primary price; otherwise show "Lifetime" since the
            // AppSumo credit isn't a recurring charge.
            if (hasStripeSub) {
                planPrice = stripePrice;
                planPricePeriod = stripePricePeriod;
            } else {
                planPrice = "Lifetime";
                planPricePeriod = null;
            }
        } else if (hasStripeSub) {
            planType = stripePlanType || "free";
            planName = stripeTierName ? "Trebble " + stripeTierName : "Free";
            planPrice = stripePrice;
            planPricePeriod = stripePricePeriod;
        } else if (hasNonDefaultMinutesWithoutPaidPlan) {
            // Non-lifetime promo / gift / partner credit — also "Custom".
            // Only the HOUR quota reflects the bumped value (sourced from
            // info.totalMinutesAvailablePerMonth via hoursPerMonth). Storage
            // and editor limits stay at the Free tier — see the planLimitKey
            // resolution below, which routes this case to FALLBACK_PLAN_FEATURES.free.
            // Rationale: redeem codes only bumped MINUTES; we don't hand out
            // free Starter/Pro storage or extra seats the user didn't earn.
            // No recurring price surfaces because the credit isn't a paid
            // subscription. The lifetime disclaimer is gated on isLifetime
            // so it correctly does NOT render here (this credit may expire
            // / cycle, depending on how the redeem code was issued).
            planType = "custom";
            planName = "Custom";
            planPrice = null;
            planPricePeriod = null;
        } else {
            planType = "free";
            planName = "Free";
            planPrice = "Free";
            planPricePeriod = null;
        }

        // Hours used this month = total - left. Both come from the same
        // /getNumberOfMinutesOfTranscriptionLeft response, so they're
        // already aligned to the same billing cycle.
        var minutesLeft = toNumber(info.transcriptionMinutesLeft);
        var hoursUsedThisMonth = (totalMinutes !== null && minutesLeft !== null)
            ? Math.max(0, Math.round(((totalMinutes - minutesLeft) / 60) * 10) / 10)
            : null;

        // Storage / editor limits — pulled from WorkspacePricingCatalog's
        // per-tier fallback table so the Usage card renders something
        // meaningful even on the synthesized default workspace (which has
        // no Workspace doc and therefore no usage snapshot from the
        // /api/v1/workspace/billing/getUsage endpoint).
        var planLimitKey;
        if (planType === 'custom' && isLifetime) {
            // AppSumo lifetime (alone OR layered on Stripe) — use the tier
            // equivalent the AppSumo redeem maps to (pro/starter). The
            // hour total has already been summed across Stripe + AppSumo
            // by the backend; storage/editors come from this tier's row.
            planLimitKey = tierEquivalent === 'pro' ? 'pro' : 'starter';
        } else if (planType === 'custom') {
            // Non-lifetime promo / gift / partner credit. The redeem
            // bumped MINUTES only — storage and editors weren't promised,
            // so we keep them at the Free defaults rather than handing
            // out free Starter/Pro storage + extra seats. Hour quota
            // comes from totalMinutesAvailablePerMonth (already correct).
            planLimitKey = 'free';
        } else if (planType === 'free') {
            planLimitKey = 'free';
        } else {
            planLimitKey = planType; // 'starter' | 'pro' | 'team'
        }
        var planLimits = WorkspacePricingCatalog.FALLBACK_PLAN_FEATURES[planLimitKey]
            || WorkspacePricingCatalog.FALLBACK_PLAN_FEATURES.free;
        var storageLimitGB = planLimits.storageGB || null;
        var editorsLimit = planLimits.editors || 1;

        // Personal storage usage. Returned by /api/v1/storage/usage. Shape
        // is StorageService.getUsage's UsageResponse:
        //   quotaUsedBytes: activeBytes + trashedBytes + reservedBytes
        //   quotaLimitBytes: per-tier cap from PlanLimits resolver
        // The endpoint returns 503 when STORAGE_SERVICE_ENABLED=false; in
        // that case the controller catch hands us null (see
        // _loadDefaultWorkspaceData). null → leave storageUsedGB null,
        // UsageBar renders "--" which is the correct UX for "unknown".
        var storageUsedGB = null;
        if (storageUsage && typeof storageUsage.quotaUsedBytes === 'number') {
            storageUsedGB = Math.round((storageUsage.quotaUsedBytes / (1024 * 1024 * 1024)) * 10) / 10;
        }
        // If the endpoint reported a quota limit, prefer that over the
        // catalog fallback (covers tier overrides / hour-pack add-ons).
        if (storageUsage && typeof storageUsage.quotaLimitBytes === 'number' && storageUsage.quotaLimitBytes > 0) {
            storageLimitGB = Math.round((storageUsage.quotaLimitBytes / (1024 * 1024 * 1024)) * 10) / 10;
        }

        // Payment method details (only meaningful for stripe subs).
        var paymentMethod = null;
        var hasPaymentMethod = false;
        if (hasStripeSub && stripeSubscription.displayablePaymentMethod) {
            paymentMethod = stripeSubscription.displayablePaymentMethod;
            hasPaymentMethod = true;
        }

        return {
            isPersonal: true,
            workspaceId: null,
            workspaceName: this._getDefaultWorkspaceDisplayName(),
            logoUrl: null,
            plan: {
                planType: planType,
                name: planName,
                price: planPrice,
                pricePeriod: planPricePeriod,
                hours: hoursPerMonth !== null ? hoursPerMonth : null,
                storage: storageLimitGB,
                editors: editorsLimit,
                reviewerLabel: "—",
                hasStripeSubscription: hasStripeSub,
                isLifetime: isLifetime,
                lifetimeTierEquivalent: tierEquivalent,
                paymentMethod: paymentMethod,
                paymentExpiry: null,
                hasPaymentMethod: hasPaymentMethod
            },
            usage: {
                // Field names match the canonical shape produced by
                // _resolveUsageData (real workspaces) and consumed by
                // WsUsagePanel: hoursUsed / hoursTotal / storageUsedGB /
                // storageTotalGB / editorsUsed / editorsTotal. Default-
                // workspace synthesis must use the same names — otherwise
                // the UsageBar reads `undefined` and renders "-- / --".
                hoursUsed: hoursUsedThisMonth,
                hoursTotal: hoursPerMonth,
                // storageUsedGB sourced from /api/v1/storage/usage above.
                // Resolves to null when STORAGE_SERVICE_ENABLED=false on
                // the backend (returns 503), or when the user has no
                // UserStorageCache row yet (legacy users — use the
                // backfill script in scripts/staging/backfill-user-storage-cache.js).
                storageUsedGB: storageUsedGB,
                storageTotalGB: storageLimitGB,
                editorsUsed: 1,           // self
                editorsTotal: editorsLimit,
                periodStart: usagePeriodStart,
                periodEnd: usagePeriodEnd,
                renewalDate: usagePeriodEnd
            },
            reviewerUsage: {}
        };
    },

    _getDefaultWorkspaceDisplayName: function () {
        var userInfo = this._userInfo;
        if (userInfo) {
            // Prefer first name; fall back to username; fall back to "My workspace".
            var firstName = userInfo.getFirstName ? userInfo.getFirstName() : null;
            if (firstName) return firstName + "'s workspace";
            var username = userInfo.getUsername ? userInfo.getUsername() : null;
            if (username) return username;
        }
        return "My workspace";
    },

    _refreshWorkspaceData: function (workspaceId) {
        var resolvedWorkspaceId = workspaceId ||
            (this._workspace && !this._workspace.isPersonal ? this._workspace.workspaceId : null) ||
            LocalStorageHelper.getInstance().getCurrentWorkspaceId();
        if (!resolvedWorkspaceId) {
            // Default workspace — refresh the synthesized view-model.
            return this._loadDefaultWorkspaceData();
        }
        return this._loadWorkspaceDetails(resolvedWorkspaceId);
    },

    _loadWorkspaceData: function () {
        try {
            var currentWorkspaceId = LocalStorageHelper.getInstance().getCurrentWorkspaceId();
            if (this._shouldUseSynthesizedDefaultWorkspace(currentWorkspaceId)) {
                // No real workspace context, or a free/custom default-personal
                // row that still gets billing from creator-sub/AppSumo data.
                return this._loadDefaultWorkspaceData();
            }
            return this._loadWorkspaceDetails(currentWorkspaceId) || RSVP.Promise.resolve();
        } catch (e) {
            console.error("Error in _loadWorkspaceData:", e);
            return RSVP.Promise.resolve();
        }
    },

    _loadWorkspaceDetails: function (workspaceId) {
        var loadVersion = this._beginWorkspaceLoad();
        return TrebbleClientAPIHelper.getInstance().getMyWorkspaces().then((function (workspaces) {
            if (!this._isWorkspaceLoadCurrent(workspaceId, loadVersion)) {
                return null;
            }
            var workspaceContext = LocalStorageHelper.getInstance().getWorkspaceContext() || {};
            var ws = workspaces && workspaces.find(function (w) {
                return (w.workspaceId || w._id) === workspaceId;
            });
            var workspaceSummary = ws || {
                workspaceId: workspaceId,
                workspaceName: workspaceContext.workspaceName,
                myRole: workspaceContext.myRole,
                isAdmin: workspaceContext.isAdmin,
                workspaceStatus: workspaceContext.workspaceStatus,
                minutesUsedThisMonth: workspaceContext.minutesUsedThisMonth,
                minutesTotal: workspaceContext.minutesTotal,
            };
            return this._loadWorkspaceSnapshot(workspaceId, workspaceSummary, loadVersion);
        }).bind(this)).catch(function (error) {
            console.error("Error loading workspace details:", error);
            if (!this._isWorkspaceLoadCurrent(workspaceId, loadVersion)) {
                return null;
            }
            var workspaceContext = LocalStorageHelper.getInstance().getWorkspaceContext() || {};
            return this._loadWorkspaceSnapshot(workspaceId, {
                workspaceId: workspaceId,
                workspaceName: workspaceContext.workspaceName,
                myRole: workspaceContext.myRole,
                isAdmin: workspaceContext.isAdmin,
                workspaceStatus: workspaceContext.workspaceStatus,
                minutesUsedThisMonth: workspaceContext.minutesUsedThisMonth,
                minutesTotal: workspaceContext.minutesTotal,
            }, loadVersion);
        }.bind(this));
    },

    _loadWorkspaceSnapshot: function (workspaceId, workspaceSummary, loadVersion) {
        if (!this._isWorkspaceLoadCurrent(workspaceId, loadVersion)) {
            return RSVP.Promise.resolve(null);
        }
        var summary = workspaceSummary || { workspaceId: workspaceId };
        this._userRole = this._mapRole(summary.myRole, summary.isAdmin);
        this._workspace = this._buildWorkspaceFromSources(summary, null, null, null);
        this._members = [];
        this._reviewers = [];
        this._cancelWorkspaceReviewersLoad();
        this._updateView();

        return RSVP.hash({
            workspaceDetails: TrebbleClientAPIHelper.getInstance().getWorkspace({ workspaceId: workspaceId }).catch(function (error) {
                console.error("Error loading workspace summary:", error);
                return null;
            }),
            membersResponse: TrebbleClientAPIHelper.getInstance().listWorkspaceMembers({ workspaceId: workspaceId }).catch(function (error) {
                console.error("Error loading workspace members:", error);
                return null;
            }),
            usageResponse: TrebbleClientAPIHelper.getInstance().getWorkspaceUsage({ workspaceId: workspaceId }).catch(function (error) {
                console.error("Error loading workspace usage:", error);
                return null;
            }),
        }).then((function (results) {
            if (!this._isWorkspaceLoadCurrent(workspaceId, loadVersion)) {
                return null;
            }
            var memberMeta = this._normalizeMembersResponse(results.membersResponse);
            this._members = memberMeta.members;
            this._workspaceUsage = this._buildChangePlanUsageSnapshot(
                results.workspaceDetails,
                results.usageResponse,
                memberMeta
            );
            this._workspace = this._buildWorkspaceFromSources(
                summary,
                results.workspaceDetails,
                memberMeta,
                results.usageResponse
            );
            this._userRole = this._mapRole(
                getFirstValueFromSources([results.workspaceDetails, summary], ["myRole", "role"]),
                getFirstValueFromSources([results.workspaceDetails, summary], ["isAdmin"])
            );
            this._applyPerMemberUsage(results.usageResponse);
            this._updateView();
            this._loadWorkspaceReviewers(workspaceId, loadVersion);
            return this._workspace;
        }).bind(this));
    },

    _loadWorkspaceReviewers: function (workspaceId, loadVersion) {
        this._cancelWorkspaceReviewersLoad();
        var reviewerLoadPromise = TrebbleClientAPIHelper.getInstance().listWorkspaceReviewers({ workspaceId: workspaceId });
        this._reviewerLoadPromise = reviewerLoadPromise;
        return reviewerLoadPromise.then((function (reviewersResponse) {
            if (this._reviewerLoadPromise === reviewerLoadPromise) {
                this._reviewerLoadPromise = null;
            }
            if (!this._isWorkspaceLoadCurrent(workspaceId, loadVersion)) {
                return null;
            }
            this._reviewers = this._normalizeReviewersResponse(reviewersResponse);
            this._updateView();
            return this._reviewers;
        }).bind(this)).catch(function (error) {
            if (this._reviewerLoadPromise === reviewerLoadPromise) {
                this._reviewerLoadPromise = null;
            }
            console.error("Error loading workspace reviewers:", error);
            if (this._isWorkspaceLoadCurrent(workspaceId, loadVersion) && typeof window !== "undefined" && typeof window.alertWarningMessage === "function") {
                window.alertWarningMessage("Could not load reviewers. Please refresh to try again.");
            }
            return null;
        }.bind(this));
    },

    _normalizeMembersResponse: function (response) {
        var memberArray = Array.isArray(response)
            ? response
            : (response && Array.isArray(response.members) ? response.members : []);
        var currentUserId = this._userInfo ? this._userInfo.getUserId() : null;
        var normalizedMembers = memberArray.map((function (member) {
            var user = member && member.user ? member.user : null;
            var resolvedUserId = getFirstValueFromSources([member, user], ["userUID", "userId", "_id"]);
            var name = getFirstValueFromSources(
                [member, user],
                ["username", "name", "firstName", "email"]
            );
            var email = getFirstValueFromSources([member, user], ["email"]);
            return {
                userId: resolvedUserId || null,
                memberRecordId: member.memberRecordId || member._id,
                name: name,
                email: email,
                role: this._mapRole(member.role, member.isAdmin),
                status: member.status || null,
                profileImageUrl: getFirstValueFromSources([member, user], ["profileImageUrl"]),
                isYou: resolvedUserId === currentUserId,
                hoursUsed: null,
                storageUsedGB: null,
            };
        }).bind(this));
        var seatLimitValue = getFirstValueFromSources(
            [response],
            ["seatLimit", "seatsTotal", "seatCount.maxMemberSeats", "seatCount.total"]
        );
        var seatUsedValue = getFirstValueFromSources(
            [response],
            ["seatUsed", "seatsUsed", "seatCount.currentMemberSeats", "seatCount.used"]
        );
        var activeEditorSeats = countSeatConsumingMembers(normalizedMembers);
        var pendingEditorInvitesCount = countPendingEditorSeatInvites(normalizedMembers);
        var derivedSeatUsed = activeEditorSeats + pendingEditorInvitesCount;
        var seatLimit = isUnlimitedValue(seatLimitValue) ? null : toNumber(seatLimitValue);
        var seatUsed = toNumber(seatUsedValue);
        seatUsed = seatUsed === null ? derivedSeatUsed : Math.max(seatUsed, derivedSeatUsed);
        return {
            members: normalizedMembers,
            seatLimit: seatLimit,
            seatUsed: seatUsed,
            activeEditorSeats: activeEditorSeats,
            pendingEditorInvitesCount: pendingEditorInvitesCount,
        };
    },

    _normalizeReviewersResponse: function (response) {
        var reviewerArray = Array.isArray(response)
            ? response
            : (response && Array.isArray(response.reviewers) ? response.reviewers : []);
        return reviewerArray.map(function (reviewer) {
            var user = reviewer && reviewer.user ? reviewer.user : null;
            var userId = getFirstValueFromSources([reviewer, user], ["userUID", "userId", "_id"]);
            var email = getFirstValueFromSources([reviewer, user], ["email"]);
            var name = getFirstValueFromSources([reviewer, user], ["name", "username", "firstName", "email"]);
            return {
                reviewerId: reviewer.reviewerId || email || userId,
                userUID: userId || null,
                name: name || email || "Reviewer",
                email: email || null,
                status: reviewer.status || null,
                activeProjectCount: toNumber(reviewer.activeProjectCount) || 0,
                pendingInviteCount: toNumber(reviewer.pendingInviteCount) || 0,
                profileImageUrl: getFirstValueFromSources([reviewer, user], ["profileImageUrl"]),
                projects: Array.isArray(reviewer.projects) ? reviewer.projects : [],
            };
        });
    },

    _mapRole: function (role, isAdmin) {
        if (role === "owner") return "owner";
        if (role === "viewer") return "viewer";
        if (role === "reviewer") return "reviewer";
        if (role === "admin") return "admin";
        if (isAdmin) return "admin";
        return "editor";
    },

    _getPlanDefaults: function (workspaceSummary, workspaceDetails) {
        var planKey = normalizePlanKey(
            getFirstValueFromSources([workspaceDetails, workspaceSummary], ["planType"])
        );
        return planKey && PLAN_DEFAULTS[planKey] ? PLAN_DEFAULTS[planKey] : null;
    },

    _resolveHoursUsed: function (workspaceSummary, workspaceDetails, usageData) {
        var directHours = getFirstValueFromSources(
            [usageData, workspaceDetails, workspaceSummary],
            ["hoursUsed", "usedHours", "usage.hoursUsed", "usage.usedHours"]
        );
        if (directHours !== null) {
            return roundToOneDecimal(directHours);
        }
        var minutesUsed = getFirstValueFromSources(
            [usageData, workspaceDetails, workspaceSummary],
            ["used", "minutesUsedThisMonth", "usedMinutes", "minutesUsed", "usage.used", "usage.minutesUsedThisMonth", "usage.usedMinutes", "usage.minutesUsed"]
        );
        return minutesToHours(minutesUsed);
    },

    _resolveHoursLimit: function (workspaceSummary, workspaceDetails, usageData, planDefaults) {
        var directHours = getFirstValueFromSources(
            [workspaceDetails, workspaceSummary],
            ["hoursTotal", "totalHours", "planLimits.hours", "effectiveLimits.hours", "usage.hoursTotal", "usage.totalHours"]
        );
        if (directHours !== null) {
            return roundToOneDecimal(directHours);
        }
        var minutesLimit = getFirstValueFromSources(
            [workspaceDetails, workspaceSummary],
            ["minutesTotal", "totalMinutes", "planLimits.minutes", "effectiveLimits.minutes", "usage.minutesTotal", "usage.totalMinutes"]
        );
        if (minutesLimit !== null) {
            return minutesToHours(minutesLimit);
        }
        var usageHours = toNumber(getFirstValueFromSources([usageData], ["hoursTotal", "totalHours"]));
        if (usageHours !== null && usageHours > 0) {
            return roundToOneDecimal(usageHours);
        }
        var usageMinutes = toNumber(getFirstValueFromSources([usageData], ["total", "minutesTotal", "totalMinutes"]));
        if (usageMinutes !== null && usageMinutes > 0) {
            return minutesToHours(usageMinutes);
        }
        return planDefaults && hasValue(planDefaults.hours) ? planDefaults.hours : null;
    },

    _resolveStorageUsedGB: function (workspaceSummary, workspaceDetails, usageData) {
        var storageUsedGB = getFirstValueFromSources(
            [usageData, workspaceDetails, workspaceSummary],
            ["storageUsedGB", "usedStorageGB", "usage.storageUsedGB", "usage.usedStorageGB"]
        );
        if (storageUsedGB !== null) {
            return roundToOneDecimal(storageUsedGB);
        }
        var storageUsedBytes = getFirstValueFromSources(
            [workspaceDetails, usageData, workspaceSummary],
            ["storageUsedBytes", "usage.storageUsedBytes", "storage.usedBytes", "usage.usedStorageBytes"]
        );
        return bytesToGigabytes(storageUsedBytes);
    },

    _resolveStorageLimitGB: function (workspaceSummary, workspaceDetails, usageData, planDefaults, options) {
        var storageLimitGB = getFirstValueFromSources(
            [workspaceDetails, workspaceSummary],
            [
                "storageTotalGB",
                "storageLimitGB",
                "totalStorageGB",
                "planLimits.storageGB",
                "effectiveLimits.storageGB",
                "usage.storageTotalGB",
                "usage.totalStorageGB"
            ]
        );
        if (storageLimitGB !== null) {
            return roundToOneDecimal(storageLimitGB);
        }
        var storageLimitBytes = getFirstValueFromSources(
            [workspaceDetails, workspaceSummary],
            [
                "storageTotalBytes",
                "storageLimitBytes",
                "totalStorageBytes",
                "planLimits.storageBytes",
                "effectiveLimits.storageBytes",
                "usage.storageTotalBytes",
                "usage.totalStorageBytes"
            ]
        );
        if (storageLimitBytes !== null) {
            return bytesToGigabytes(storageLimitBytes);
        }
        var usageStorageGB = toNumber(getFirstValueFromSources([usageData], ["storageTotalGB", "totalStorageGB"]));
        if (usageStorageGB !== null && usageStorageGB > 0) {
            return roundToOneDecimal(usageStorageGB);
        }
        var usageStorageBytes = toNumber(getFirstValueFromSources([usageData], ["storageTotalBytes", "totalStorageBytes"]));
        if (usageStorageBytes !== null && usageStorageBytes > 0) {
            return bytesToGigabytes(usageStorageBytes);
        }
        return options && options.allowPlanFallback && planDefaults && hasValue(planDefaults.storageGB)
            ? planDefaults.storageGB
            : null;
    },

    _resolveUsagePeriodStart: function (workspaceSummary, workspaceDetails, usageData) {
        return getFirstValueFromSources(
            [usageData],
            ["periodStart", "currentPeriodStart", "cycleStartDate", "usage.periodStart", "usage.currentPeriodStart", "usage.cycleStartDate"]
        ) || getFirstValueFromSources(
            [workspaceDetails, workspaceSummary],
            ["usage.periodStart"]
        );
    },

    _resolveUsagePeriodEnd: function (workspaceSummary, workspaceDetails, usageData) {
        return getFirstValueFromSources(
            [usageData],
            ["periodEnd", "currentPeriodEnd", "cycleEndDate", "usage.periodEnd", "usage.currentPeriodEnd", "usage.cycleEndDate"]
        ) || getFirstValueFromSources(
            [workspaceDetails, workspaceSummary],
            ["usage.periodEnd"]
        );
    },

    _resolveEditorLimit: function (workspaceSummary, workspaceDetails, memberMeta, planDefaults, options) {
        if (memberMeta && memberMeta.seatLimit !== null && memberMeta.seatLimit !== undefined) {
            return memberMeta.seatLimit;
        }
        var editorLimitValue = getFirstValueFromSources(
            [workspaceDetails, workspaceSummary],
            [
                "seatsTotal",
                "seatLimit",
                "seatCount.maxMemberSeats",
                "seatCount.total",
                "maxMemberSeats",
                "planLimits.editors",
                "planLimits.members",
                "effectiveLimits.editors",
                "effectiveLimits.members",
                "usage.editorsTotal"
            ]
        );
        if (isUnlimitedValue(editorLimitValue)) {
            return null;
        }
        var editorLimit = toNumber(editorLimitValue);
        if (editorLimit !== null) {
            return editorLimit;
        }
        return options && options.allowPlanFallback && planDefaults && hasValue(planDefaults.editors)
            ? planDefaults.editors
            : null;
    },

    _resolveEditorUsed: function (workspaceSummary, workspaceDetails, memberMeta) {
        if (memberMeta && memberMeta.seatUsed !== null && memberMeta.seatUsed !== undefined) {
            return memberMeta.seatUsed;
        }
        var editorUsedValue = getFirstValueFromSources(
            [workspaceDetails, workspaceSummary],
            ["seatsUsed", "seatUsed", "seatCount.currentMemberSeats", "seatCount.used", "currentMemberSeats", "usage.editorsUsed"]
        );
        var editorUsed = toNumber(editorUsedValue);
        if (editorUsed !== null) {
            return editorUsed;
        }
        return memberMeta && memberMeta.members ? countSeatConsumingMembers(memberMeta.members) : null;
    },

    _resolveReviewerCurrent: function (workspaceSummary, workspaceDetails, usageData) {
        var reviewerCurrentValue = getFirstValueFromSources(
            [workspaceDetails, usageData, workspaceSummary],
            [
                "reviewerUsage.current",
                "reviewerUsage.currentCount",
                "reviewersUsedThisMonth",
                "reviewersThisBillingCycle",
                "reviewerCount.current",
                "reviewerCount.currentCount",
                "usage.reviewersUsedThisMonth",
                "usage.reviewersThisBillingCycle"
            ]
        );
        return toNumber(reviewerCurrentValue);
    },

    _resolveReviewerLimit: function (workspaceSummary, workspaceDetails, usageData, planDefaults, options) {
        var reviewerLimitValue = getFirstValueFromSources(
            [workspaceDetails, workspaceSummary, usageData],
            [
                "reviewerUsage.limit",
                "reviewerUsage.maxCount",
                "reviewerLimit",
                "maxReviewers",
                "reviewerCount.max",
                "reviewerCount.maxCount",
                "planLimits.reviewers",
                "effectiveLimits.reviewers",
                "usage.reviewerUsage.limit",
                "usage.reviewerUsage.maxCount",
                "usage.reviewersTotal",
                "usage.maxReviewers"
            ]
        );
        if (isUnlimitedValue(reviewerLimitValue)) {
            return "Unlimited";
        }
        var reviewerLimit = toNumber(reviewerLimitValue);
        if (reviewerLimit !== null) {
            return reviewerLimit;
        }
        if (hasValue(reviewerLimitValue)) {
            return reviewerLimitValue;
        }
        return options && options.allowPlanFallback && planDefaults && hasValue(planDefaults.reviewers)
            ? planDefaults.reviewers
            : null;
    },

    _resolvePlanData: function (workspaceSummary, workspaceDetails, usageData, memberMeta) {
        var planDefaults = this._getPlanDefaults(workspaceSummary, workspaceDetails);
        // Plan metadata may fall back to tier defaults when backend overrides are absent.
        var planFallbackOptions = { allowPlanFallback: true };
        var planKey = normalizePlanKey(
            getFirstValueFromSources([workspaceDetails, workspaceSummary], ["planType"])
        );
        var billingCycle = getFirstValueFromSources(
            [workspaceDetails, workspaceSummary],
            ["billingCycle", "subscription.billingCycle", "plan.billingCycle"]
        );
        var planName = getFirstValueFromSources(
            [workspaceDetails, workspaceSummary],
            ["planName", "plan.name", "subscription.planName"]
        ) || (planDefaults && planDefaults.name) || titleCase(planKey) || "Workspace plan";
        var displayPriceValue = getFirstValueFromSources(
            [workspaceDetails, workspaceSummary],
            ["planPriceDisplay", "priceDisplay", "subscription.priceDisplay", "plan.priceDisplay"]
        );
        var rawPriceValue = displayPriceValue !== null
            ? null
            : getFirstValueFromSources(
                [workspaceDetails, workspaceSummary],
                ["plan.price", "price"]
        );
        var planPrice = formatCurrencyForDisplay(displayPriceValue !== null ? displayPriceValue : rawPriceValue);
        var pricePeriod = null;
        var isYearlyBillingCycle = billingCycle === "annual" || billingCycle === "yearly";
        if (rawPriceValue !== null && !priceValueHasExplicitPeriod(rawPriceValue)) {
            pricePeriod = isYearlyBillingCycle ? "/year" : "/month";
        }
        if (!planPrice && planDefaults && hasValue(planDefaults.price)) {
            var fallbackPlanPrice = WorkspacePricingCatalog.FALLBACK_PLAN_PRICES[planKey];
            planPrice = isYearlyBillingCycle && fallbackPlanPrice
                ? WorkspacePricingCatalog.formatCurrencyAmount(fallbackPlanPrice.yearly, fallbackPlanPrice.currency).replace(/\.00$/, "")
                : planDefaults.price;
            pricePeriod = isYearlyBillingCycle ? "/year" : "/month";
        }
        if (!planPrice && (planKey === "custom" || planKey === "enterprise")) {
            planPrice = "Custom";
            pricePeriod = null;
        }

        var hoursLimit = this._resolveHoursLimit(workspaceSummary, workspaceDetails, usageData, planDefaults);
        var storageLimitGB = this._resolveStorageLimitGB(
            workspaceSummary,
            workspaceDetails,
            usageData,
            planDefaults,
            planFallbackOptions
        );
        var editorLimit = this._resolveEditorLimit(
            workspaceSummary,
            workspaceDetails,
            memberMeta,
            planDefaults,
            planFallbackOptions
        );
        var reviewerLimit = this._resolveReviewerLimit(
            workspaceSummary,
            workspaceDetails,
            usageData,
            planDefaults,
            planFallbackOptions
        );
        var reviewerLabel = null;
        if (isUnlimitedValue(reviewerLimit)) {
            reviewerLabel = "Unlimited reviewers";
        } else if (hasValue(reviewerLimit)) {
            var formattedReviewerLimit = formatNumberForDisplay(reviewerLimit);
            reviewerLabel = formattedReviewerLimit !== null
                ? formattedReviewerLimit + " reviewers"
                : String(reviewerLimit);
        }
        var rawHasStripeSubscription = getFirstValueFromSources(
            [workspaceDetails, workspaceSummary],
            ["hasStripeSubscription", "billing.hasStripeSubscription"]
        );

        return {
            planType: planKey,
            name: planName,
            price: planPrice,
            pricePeriod: pricePeriod,
            billingCycle: billingCycle || "monthly",
            hours: hoursLimit !== null ? formatHoursForDisplay(hoursLimit) : null,
            storage: storageLimitGB !== null ? formatGigabytesForDisplay(storageLimitGB) : null,
            editors: editorLimit !== null ? formatNumberForDisplay(editorLimit) : null,
            reviewers: reviewerLimit,
            reviewerLabel: reviewerLabel,
            paymentMethod: getFirstValueFromSources(
                [workspaceDetails, workspaceSummary],
                ["paymentMethod", "billing.paymentMethod", "defaultPaymentMethod.label", "defaultPaymentMethod.summary"]
            ),
            paymentExpiry: getFirstValueFromSources(
                [workspaceDetails, workspaceSummary],
                ["paymentExpiry", "billing.paymentExpiry", "defaultPaymentMethod.expiryDisplay"]
            ),
            // Drives the Settings > Billing "Add Payment Method" vs "Update"
            // button and the controller's branching between Stripe Checkout
            // (setup mode) and the Customer Portal. Without these flags the
            // UI can only look at the rendered paymentMethod string, which
            // is ambiguous (empty on both "no customer" and "customer with
            // no card") and cannot drive the correct Stripe flow.
            hasStripeCustomer: getFirstValueFromSources(
                [workspaceDetails, workspaceSummary],
                ["hasStripeCustomer", "billing.hasStripeCustomer"]
            ) === true,
            hasPaymentMethod: getFirstValueFromSources(
                [workspaceDetails, workspaceSummary],
                ["hasPaymentMethod", "billing.hasPaymentMethod"]
            ) === true,
            // Drives the Members Invite gate (and the Buy More Hours gate).
            // The synthesized default workspace view-model already exposes
            // these two flags; the real-workspace path was missing them, so
            // post-upgrade Starter / Pro / Team owners saw the "Upgrade to
            // a paid plan" tooltip on the Invite Member button despite
            // having an active Stripe sub.
            //
            // hasStripeSubscription: prefer an explicit BE value when present.
            // If absent, derive from planType so post-upgrade paid workspaces
            // still unlock paid Stripe-subscription actions.
            hasStripeSubscription: rawHasStripeSubscription === null
                ? (planKey === "starter" || planKey === "pro" || planKey === "team" || planKey === "enterprise")
                : rawHasStripeSubscription === true,
            isLifetime: getFirstValueFromSources(
                [workspaceDetails, workspaceSummary],
                ["isLifetime", "billing.isLifetime"]
            ) === true,
        };
    },

    _resolveUsageData: function (workspaceSummary, workspaceDetails, usageData, memberMeta) {
        var planDefaults = this._getPlanDefaults(workspaceSummary, workspaceDetails);
        // Live usage bars should prefer unknown totals over guessed tier defaults.
        var liveUsageOptions = { allowPlanFallback: false };
        var periodStart = this._resolveUsagePeriodStart(workspaceSummary, workspaceDetails, usageData);
        var periodEnd = this._resolveUsagePeriodEnd(workspaceSummary, workspaceDetails, usageData);
        return {
            hoursUsed: this._resolveHoursUsed(workspaceSummary, workspaceDetails, usageData),
            hoursTotal: this._resolveHoursLimit(workspaceSummary, workspaceDetails, usageData, planDefaults),
            storageUsedGB: this._resolveStorageUsedGB(workspaceSummary, workspaceDetails, usageData),
            storageTotalGB: this._resolveStorageLimitGB(
                workspaceSummary,
                workspaceDetails,
                usageData,
                planDefaults,
                liveUsageOptions
            ),
            editorsUsed: this._resolveEditorUsed(workspaceSummary, workspaceDetails, memberMeta),
            editorsTotal: this._resolveEditorLimit(
                workspaceSummary,
                workspaceDetails,
                memberMeta,
                planDefaults,
                liveUsageOptions
            ),
            periodStart: periodStart,
            periodEnd: periodEnd,
            renewalDate: periodEnd,
        };
    },

    _resolveReviewerUsage: function (workspaceSummary, workspaceDetails, usageData) {
        var planDefaults = this._getPlanDefaults(workspaceSummary, workspaceDetails);
        var liveUsageOptions = { allowPlanFallback: false };
        return {
            current: this._resolveReviewerCurrent(workspaceSummary, workspaceDetails, usageData),
            limit: this._resolveReviewerLimit(
                workspaceSummary,
                workspaceDetails,
                usageData,
                planDefaults,
                liveUsageOptions
            ),
        };
    },

    _buildChangePlanUsageSnapshot: function (workspaceDetails, usageData, memberMeta) {
        // Used by the Change Plan modal (to compute downgrade exceedances) and
        // by the Hour Pack popup (to show current + projected totals). Pulls
        // from the same raw responses used by the live usage panel so values
        // stay in sync.
        var hoursUsed = this._resolveHoursUsed(null, workspaceDetails, usageData);
        var hoursTotal = this._resolveHoursLimit(null, workspaceDetails, usageData, null);
        var storageUsedBytes = getFirstValueFromSources(
            [workspaceDetails, usageData],
            ["storageUsedBytes", "usage.storageUsedBytes", "storage.usedBytes", "usage.usedStorageBytes"]
        );
        var storageUsedGB = this._resolveStorageUsedGB(null, workspaceDetails, usageData);
        if ((storageUsedBytes === null || storageUsedBytes === undefined) && storageUsedGB !== null) {
            storageUsedBytes = Number(storageUsedGB) * 1024 * 1024 * 1024;
        }
        var editorsCount = this._resolveEditorUsed(null, workspaceDetails, memberMeta);
        var activeEditorSeats = memberMeta && typeof memberMeta.activeEditorSeats === "number"
            ? memberMeta.activeEditorSeats
            : null;
        var pendingEditorInvitesCount = memberMeta && typeof memberMeta.pendingEditorInvitesCount === "number"
            ? memberMeta.pendingEditorInvitesCount
            : 0;
        if (editorsCount === null && activeEditorSeats !== null) {
            editorsCount = activeEditorSeats + pendingEditorInvitesCount;
        }
        var reviewersCount = this._resolveReviewerCurrent(null, workspaceDetails, usageData);
        return {
            editorsCount: editorsCount || 0,
            activeEditorSeats: activeEditorSeats !== null ? activeEditorSeats : (editorsCount || 0),
            pendingEditorInvitesCount: pendingEditorInvitesCount,
            reviewersCount: reviewersCount || 0,
            hoursUsed: hoursUsed || 0,
            hoursTotal: hoursTotal,
            storageUsedBytes: storageUsedBytes || 0
        };
    },

    _buildWorkspaceFromSources: function (workspaceSummary, workspaceDetails, memberMeta, usageData) {
        return {
            workspaceId: getFirstValueFromSources([workspaceDetails, workspaceSummary], ["workspaceId", "_id"]) || null,
            workspaceName: getFirstValueFromSources([workspaceDetails, workspaceSummary], ["workspaceName", "name"]) || "Workspace",
            logoUrl: getFirstValueFromSources([workspaceDetails, workspaceSummary], ["logoUrl"]) || null,
            plan: this._resolvePlanData(workspaceSummary, workspaceDetails, usageData, memberMeta),
            usage: this._resolveUsageData(workspaceSummary, workspaceDetails, usageData, memberMeta),
            reviewerUsage: this._resolveReviewerUsage(workspaceSummary, workspaceDetails, usageData),
        };
    },

    _applyPerMemberUsage: function (usageData) {
        if (!usageData || !Array.isArray(usageData.perMember) || !this._members || this._members.length === 0) {
            return;
        }
        usageData.perMember.forEach((function (perMemberUsage) {
            var usageIdentifiers = getNormalizedIdentifierValues(perMemberUsage, ["userUID", "userId", "_id"]);
            var member = null;
            if (usageIdentifiers.length > 0) {
                member = this._members.find(function (existingMember) {
                    var existingMemberId = normalizeIdentifier(existingMember.userId);
                    return existingMemberId !== null && usageIdentifiers.indexOf(existingMemberId) !== -1;
                });
            }
            var usageEmail = normalizeEmail(
                getFirstValueFromSources([perMemberUsage], ["email", "memberEmail", "userEmail"])
            );
            if (!member && usageEmail) {
                member = this._members.find(function (existingMember) {
                    return normalizeEmail(existingMember.email) === usageEmail;
                });
            }
            if (!member) return;

            var directHours = getFirstValueFromSources(
                [perMemberUsage],
                ["hoursUsed", "usedHours"]
            );
            var minutesUsed = getFirstValueFromSources(
                [perMemberUsage],
                ["minutesUsed", "used", "usedMinutes"]
            );
            var resolvedHoursUsed = directHours !== null
                ? roundToOneDecimal(directHours)
                : minutesToHours(minutesUsed);
            if (resolvedHoursUsed !== null) {
                member.hoursUsed = resolvedHoursUsed;
            }

            var storageUsedGB = getFirstValueFromSources(
                [perMemberUsage],
                ["storageUsedGB", "usedStorageGB"]
            );
            if (storageUsedGB !== null) {
                member.storageUsedGB = roundToOneDecimal(storageUsedGB);
                return;
            }

            var storageUsedBytes = getFirstValueFromSources(
                [perMemberUsage],
                ["storageUsedBytes", "usedStorageBytes"]
            );
            var resolvedStorageUsedGB = bytesToGigabytes(storageUsedBytes);
            if (resolvedStorageUsedGB !== null) {
                member.storageUsedGB = resolvedStorageUsedGB;
            }
        }).bind(this));
    },

    _updateView: function () {
        if (this.view) {
            this.view.updateWorkspaceData(this._workspace, this._userRole, this._members, this._reviewers);
        }
    },

    _useMockWorkspaceData: function () {
        var username = (this._userInfo && this._userInfo.getUsername) ? this._userInfo.getUsername() : "Beaudry Martin";
        var email = (this._userInfo && this._userInfo.getEmail) ? this._userInfo.getEmail() : "beaudry@trebble.fm";
        this._workspace = {
            workspaceId: "mock-ws-001",
            workspaceName: "Acme Podcasts",
            plan: {
                name: "Team",
                planType: "team",
                price: "$99.99",
                pricePeriod: "/month",
                hours: "100",
                storage: "200 GB",
                editors: "5",
                reviewers: "Unlimited",
                reviewerLabel: "Unlimited reviewers",
                paymentMethod: "Visa ending in 4242",
                paymentExpiry: "Expires 12/2027"
            },
            usage: { hoursUsed: 62, hoursTotal: 100, storageUsedGB: 84, storageTotalGB: 200, editorsUsed: 3, editorsTotal: 5 },
            reviewerUsage: { current: 3, limit: "Unlimited" },
        };
        this._userRole = "owner";
        this._members = [
            { userId: "u1", name: username, email: email, role: "owner", isYou: true, hoursUsed: 28, storageUsedGB: 42 },
            { userId: "u2", name: "Sarah Chen", email: "sarah@acme.com", role: "editor", isYou: false, hoursUsed: 22, storageUsedGB: 30 },
            { userId: "u3", name: "Jackie Chan", email: "jackie@acme.com", role: "editor", isYou: false, hoursUsed: 12, storageUsedGB: 12 },
        ];
        this._reviewers = [
            { reviewerId: "r1", name: "Maya Reviewer", email: "maya@example.com", status: "active", activeProjectCount: 2, pendingInviteCount: 0, projects: [{ projectName: "Launch edit" }, { projectName: "Client episode" }] },
        ];
        this._updateView();
    },

    buildModelAndRenderView: function () {
        return new RSVP.Promise((function (resolve, reject) {
            try {
                var initialPushEnabled = PushNotificationHelper.getInstance().isUserRegisteredForDailyNotification();
                var initialEmailEnabled = !LocalStorageHelper.getInstance().isEmailDailyDigestDisabled();
                var params = {
                    userInfo: this._userInfo,
                    workspace: this._workspace,
                    userRole: this._userRole,
                    members: this._members,
                    reviewers: this._reviewers,
                    initialPushNotificationsEnabled: initialPushEnabled,
                    initialEmailNotificationsEnabled: initialEmailEnabled,
                    callbacks: {
                        onEditProfile: this.editProfile.bind(this),
                        onChangePassword: this.changePassword.bind(this),
                        onChangeProfilePicture: this.changeProfilePicture.bind(this),
                        onLogout: this.logout.bind(this),
                        onTogglePushNotifications: this.togglePushNotifications.bind(this),
                        onToggleEmailNotifications: this.toggleEmailNotifications.bind(this),
                        onInviteMember: this.inviteMember.bind(this),
                        onRemoveMember: this.removeMember.bind(this),
                        onRemoveReviewer: this.removeReviewer.bind(this),
                        onSetMemberRole: this.setMemberRole.bind(this),
                        onLeaveWorkspace: this.leaveWorkspace.bind(this),
                        onDeleteWorkspace: this.deleteWorkspace.bind(this),
                        onUpgradePlan: this.upgradePlan.bind(this),
                        onUpdatePaymentMethod: this.updatePaymentMethod.bind(this),
                        onChangePlan: this.changePlan.bind(this),
                        onCancelSubscription: this.cancelSubscription.bind(this),
                        onPurchaseHourPack: this.purchaseHourPack.bind(this),
                    }
                };

                this.view = new SettingsPage2View(params);
                this.view.render();

                this.listenTo(this.view, "showHomePopup", this.showHomePage);
                this.listenTo(this.view, "pageshow", this.onPageShow);

                if (WORKSPACE_ENABLED) {
                    if (USE_REAL_WORKSPACE_DATA) {
                        this._loadWorkspaceData();
                    } else {
                        this._useMockWorkspaceData();
                    }
                }

                resolve();
            } catch (error) {
                reject(error);
            }
        }).bind(this));
    }
});

export default SettingsPage2Controller;
